import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button } from 'antd';

import { geolocationConstants } from '../../constants/geolocationConsts';

import ExportIcon from '../../resources/icons/ExportIcon';
import { colorPalette } from '../../resources/styles/colorPalette';

import { selectDarkMode } from '../../userBrowserSettings/store/browserSettings.selectors';

import {
    calculateOptimalRankabilityScore,
    getReadabilityLabel,
} from '../../utils/helpers/getOptimizerBasicData';

import { selectContentOptimizerMainQueryParams } from '../pages/contentOptimizer/mainPage/store/contentOptimizerMain.selectors';

import { contentOptimizerPathes } from '../../constants/queryPathes';

import { UsersContext } from '../hocs/UsersProvider';

import { axiosAPI } from '../../utils/axiosAPI';
import { getAxiosHeaders } from '../../utils/helpers/getAxiosHeaders';

import { getUserFullName } from '../../utils/helpers/getUserFullName';
import { getThemeColorClass } from '../../utils/helpers/themeHelpers';
import { getNumberRoundedToHundreds } from '../../utils/helpers/getNumberRoundedToHundreds';
import { openBasicErrorNotification } from '../../utils/helpers/openBasicErrorNotification';

import useDomainUrl from '../../utils/hooks/useDomainUrl';

const classes = {
    exportButton: 'create-new-btn',
};

const ExportOptimizerButton = () => {
    const { users } = useContext(UsersContext);
    const queryParams = useSelector(selectContentOptimizerMainQueryParams);
    const darkMode = useSelector(selectDarkMode);
    const { getDomainUrl } = useDomainUrl();

    const [isExporting, setIsExporting] = useState(false);

    const extractStateName = (geolocationCanonicalName) => {
        if (!geolocationCanonicalName) {
            return null;
        }

        const separator = ',';
        const firstCommaIndex = geolocationCanonicalName.indexOf(separator);
        const lastCommaIndex = geolocationCanonicalName.lastIndexOf(separator);

        return geolocationCanonicalName.substring(firstCommaIndex + 1, lastCommaIndex).trim();
    };

    const extractProjectNames = (projects = []) => {
        if (!projects || projects.length === 0) {
            return null;
        }

        return projects.map((project) => project.name).join(', ');
    };

    const mapOptimizersData = (result = []) => {
        if (result.length > 0) {
            const mappedData = result.map((item) => {
                const geolocationCanonicalName =
                    item.content_brief.geo_location_obj?.[
                        geolocationConstants.CANONICAL_NAME_FIELD
                    ];
                const stateName = extractStateName(geolocationCanonicalName);
                const projects = item.content_brief.projects;

                return {
                    keyword: item.content_brief.keywords,
                    language: item.content_brief.language_code,
                    country: item.content_brief.country_code,
                    city: item.content_brief.geo_location_obj?.['Name'] ?? null,
                    state: stateName,
                    createdAt: item.created_at,
                    createdBy: getUserFullName(users, item.user_id),
                    projects: extractProjectNames(projects),
                    optimizerLink: getDomainUrl(`optimizer/${item.id}`),
                    rankabilityScore: item.rankability_score.toString(),
                    wordCount: item.word_count.toString(),
                    readability: getReadabilityLabel(item.readability),
                    optimalRankabilityScore: calculateOptimalRankabilityScore(
                        item.optimals.optimal_rankability_score.max
                    ).toString(),
                    optimalWordCountMin: getNumberRoundedToHundreds(
                        item.optimals.optimal_word_count.min
                    ).toString(),
                    optimalWordCountMax: getNumberRoundedToHundreds(
                        item.optimals.optimal_word_count.max
                    ).toString(),
                };
            });

            return mappedData;
        }

        return result;
    };

    const generateReport = (mappedOptimizersData = []) => {
        let convertedOptimizersData =
            'Keyword,Language,Country,City,State/Province,Created At,Created By,Projects,Optimizer Link,Rankability Score,Word Count,Readability,Optimal Rankability Score,Optimal Word Count Min,Optimal Word Count Max\n';

        mappedOptimizersData.forEach((item) => {
            const convertedOptimizerItem = JSON.stringify(Object.values(item))
                ?.substring(1)
                ?.slice(0, -1)
                ?.replaceAll(',null', ',');

            convertedOptimizersData = convertedOptimizersData + convertedOptimizerItem + '\n';
        });

        const BOM = '\uFEFF';
        const url = window.URL.createObjectURL(
            new Blob([BOM + convertedOptimizersData], {
                type: 'text/csv;charset=utf-8',
            })
        );
        const link = document.createElement('a');
        link.target = '_blank';
        link.href = url;
        link.setAttribute('download', `Optimizers.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const handleExport = () => {
        setIsExporting(true);

        axiosAPI
            .get(
                contentOptimizerPathes.export,
                {
                    params: {
                        sort: queryParams.sort,
                        order: queryParams.order,
                        archived: false,
                        projects: queryParams.projects ?? undefined,
                        users: queryParams?.users ? JSON.stringify(queryParams?.users) : undefined,
                        dateRangePicker: queryParams?.dateRangePicker
                            ? JSON.stringify(queryParams?.dateRangePicker)
                            : undefined,
                    },
                },
                { ...getAxiosHeaders() }
            )
            .then((result) => {
                const mappedOptimizersData = mapOptimizersData(result.data);
                generateReport(mappedOptimizersData);
            })
            .catch(() => {
                openBasicErrorNotification();
            })
            .finally(() => setIsExporting(false));
    };

    return (
        <Button
            className={`${classes.exportButton} ${getThemeColorClass(darkMode)}`}
            icon={<ExportIcon color={colorPalette.colorPrimary} />}
            loading={isExporting}
            disabled={isExporting}
            type='primary'
            size='large'
            ghost
            onClick={handleExport}
        >
            Export
        </Button>
    );
};

export default ExportOptimizerButton;
